import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    IconButton,
    Stack,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Alert,
    AlertTitle,
    Divider
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import {
    addProgramUserRating,
    updateProgramUserRating,
    getProgramUserRating,
    getDropdownData
} from '../Queries';

const RatingDialog = ({ open, onClose, user, sessionType, programCode, clientCode }) => {
    const [ratingMetrics, setRatingMetrics] = useState([]);
    const [ratings, setRatings] = useState({});
    const [notes, setNotes] = useState({});
    const [description, setDescription] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [existingRatingId, setExistingRatingId] = useState(null);
    const [alert, setAlert] = useState({ open: false, severity: 'success', message: '' });

    let [userData] = useState(localStorage.getItem("userData"));
    userData = userData ? JSON.parse(userData) : userData;

    const sessionTitles = {
        'pre_program_review': 'Initial',
        'post_program_review': 'Final'
    };

    const ratingOptions = [
        { value: '5', label: '5 - Excellent' },
        { value: '4', label: '4 - Good' },
        { value: '3', label: '3 - Average' },
        { value: '2', label: '2 - Below Average' },
        { value: '1', label: '1 - Poor' }
    ];

    useEffect(() => {
        if (open && user && sessionType && programCode) {
            loadRatingMetrics();
            loadExistingRating();
        }
    }, [open, user, sessionType, programCode]);

    const handleCloseAlert = () => {
        setAlert({ open: false, severity: 'success', message: '' });
    };

    const loadRatingMetrics = async () => {
        setIsLoading(true);
        try {
            const allDropdownData = await getDropdownData();
            const metrics = allDropdownData.filter(
                item => item.type === 'program_rating_metrics' && item.client_code === clientCode
            );

            const processedMetrics = metrics.map(metric => ({
                id: metric.row_id,
                name: metric.name,
                metrics: metric.json_data?.metrics || {}
            }));

            setRatingMetrics(processedMetrics);
        } catch (error) {
            console.error('Error loading rating metrics:', error);
            setAlert({
                open: true,
                severity: 'error',
                message: 'Failed to load rating metrics.'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const loadExistingRating = async () => {
        try {
            // Create a unique ID based on user, program, and session
            const ratingId = `${programCode}_${sessionType}`;
            const existingRating = await getProgramUserRating(ratingId, user.email);

            if (existingRating && existingRating.rating_json) {
                setExistingRatingId(existingRating.id);
                const ratingData = existingRating.rating_json;

                // Populate ratings and notes from existing data
                const newRatings = {};
                const newNotes = {};

                Object.keys(ratingData).forEach(metricName => {
                    if (metricName === '_description') {
                        setDescription(ratingData[metricName] || '');
                    } else {
                        newRatings[metricName] = ratingData[metricName].rating;
                        newNotes[metricName] = ratingData[metricName].note || '';
                    }
                });

                setRatings(newRatings);
                setNotes(newNotes);
            }
        } catch (error) {
            console.error('Error loading existing rating:', error);
        }
    };

    const handleRatingChange = (metricName, value) => {
        setRatings(prev => ({
            ...prev,
            [metricName]: value
        }));
    };

    const handleNoteChange = (metricName, value) => {
        setNotes(prev => ({
            ...prev,
            [metricName]: value
        }));
    };

    const handleSubmit = async () => {
        // Validate that all metrics have ratings
        const missingRatings = ratingMetrics.filter(metric => !ratings[metric.name]);
        if (missingRatings.length > 0) {
            setAlert({
                open: true,
                severity: 'error',
                message: 'Please provide ratings for all metrics.'
            });
            return;
        }

        setIsLoading(true);
        try {
            // Build rating JSON
            const ratingJson = {
                _description: description
            };
            ratingMetrics.forEach(metric => {
                ratingJson[metric.name] = {
                    rating: ratings[metric.name],
                    note: notes[metric.name] || '',
                    metric_id: metric.id
                };
            });

            const ratingId = `${programCode}_${sessionType}`;
            const ratingData = {
                id: ratingId,
                user_id: user.email,
                rating_json: JSON.stringify(ratingJson),
                program_code: programCode,
                created_by: userData.email,
                updated_by: userData.email
            };

            if (existingRatingId) {
                // Update existing rating
                await updateProgramUserRating(ratingData);
                setAlert({
                    open: true,
                    severity: 'success',
                    message: 'Rating updated successfully!'
                });
            } else {
                // Add new rating
                await addProgramUserRating(ratingData);
                setAlert({
                    open: true,
                    severity: 'success',
                    message: 'Rating submitted successfully!'
                });
            }

            // Close dialog after a short delay
            setTimeout(() => {
                handleClose();
            }, 1500);
        } catch (error) {
            console.error('Error saving rating:', error);
            setAlert({
                open: true,
                severity: 'error',
                message: 'Failed to save rating. Please try again.'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        setRatings({});
        setNotes({});
        setDescription('');
        setExistingRatingId(null);
        setAlert({ open: false, severity: 'success', message: '' });
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    fontFamily: 'Poppins'
                }
            }}
        >
            <DialogTitle sx={{
                borderBottom: '1px solid #e0e0e0',
                pb: 2,
                fontFamily: 'Poppins',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <Box>
                    <Typography variant="h6" sx={{ fontFamily: 'Poppins', fontWeight: 600 }}>
                        {sessionTitles[sessionType]} - Rating
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                        {user?.name} ({user?.email})
                    </Typography>
                </Box>
                <IconButton onClick={handleClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 3, mt: 2 }}>
                <Stack spacing={3}>
                    {/* Alert Section */}
                    {alert.open && (
                        <Alert
                            severity={alert.severity}
                            onClose={handleCloseAlert}
                            sx={{
                                borderRadius: 2,
                                '& .MuiAlert-message': {
                                    width: '100%'
                                }
                            }}
                        >
                            <AlertTitle sx={{ fontWeight: 600 }}>
                                {alert.severity === 'success' ? 'Success' : 'Error'}
                            </AlertTitle>
                            {alert.message}
                        </Alert>
                    )}

                    {/* Description Field */}
                    <Box>
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                fontFamily: 'Poppins', 
                                fontWeight: 600, 
                                mb: 1,
                                color: '#333'
                            }}
                        >
                            Description
                        </Typography>
                        <TextField
                            multiline
                            rows={3}
                            fullWidth
                            variant="outlined"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Enter a description for this rating session..."
                            sx={{
                                '& .MuiInputBase-root': {
                                    fontFamily: 'Poppins',
                                    fontSize: '14px'
                                }
                            }}
                        />
                    </Box>

                    <Divider />

                    {ratingMetrics.length === 0 && !isLoading && (
                        <Alert severity="info">
                            <AlertTitle>No Rating Metrics</AlertTitle>
                            No rating metrics have been configured for this program yet. Please add rating metrics first.
                        </Alert>
                    )}

                    {ratingMetrics.map((metric, index) => (
                        <Box key={metric.id}>
                            {index > 0 && <Divider sx={{ my: 2 }} />}
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontFamily: 'Poppins',
                                    fontWeight: 600,
                                    mb: 2,
                                    color: '#2c3e50'
                                }}
                            >
                                {metric.name}
                            </Typography>

                            <Stack spacing={2}>
                                <FormControl
                                    fullWidth
                                    size="small"
                                    required
                                >
                                    <InputLabel sx={{ fontFamily: 'Poppins' }}>Rating</InputLabel>
                                    <Select
                                        value={ratings[metric.name] || ''}
                                        label="Rating"
                                        onChange={(e) => handleRatingChange(metric.name, e.target.value)}
                                        sx={{
                                            fontFamily: 'Poppins',
                                            fontSize: '14px'
                                        }}
                                    >
                                        {ratingOptions.map((option) => (
                                            <MenuItem
                                                key={option.value}
                                                value={option.value}
                                                sx={{ fontFamily: 'Poppins' }}
                                            >
                                                <Box>
                                                    <Typography sx={{ fontFamily: 'Poppins', fontSize: '14px' }}>
                                                        {option.label}
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            fontFamily: 'Poppins',
                                                            fontSize: '12px',
                                                            color: 'text.secondary',
                                                            mt: 0.5
                                                        }}
                                                    >
                                                        {metric.metrics[option.label]?.description || ''}
                                                    </Typography>
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <TextField
                                    fullWidth
                                    label="Notes (Optional)"
                                    value={notes[metric.name] || ''}
                                    onChange={(e) => handleNoteChange(metric.name, e.target.value)}
                                    multiline
                                    rows={3}
                                    size="small"
                                    placeholder="Add any additional notes or comments..."
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            fontFamily: 'Poppins',
                                        }
                                    }}
                                />
                            </Stack>
                        </Box>
                    ))}
                </Stack>
            </DialogContent>

            <DialogActions sx={{ borderTop: '1px solid #e0e0e0', p: 2 }}>
                <Button
                    onClick={handleClose}
                    sx={{
                        textTransform: 'none',
                        fontFamily: 'Poppins',
                        color: '#666'
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={isLoading || ratingMetrics.length === 0}
                    sx={{
                        textTransform: 'none',
                        fontFamily: 'Poppins',
                        background: "linear-gradient(45deg, #0a0068 30%, #cc4049 90%)",
                        "&:hover": {
                            background: "linear-gradient(45deg, #1565c0 30%, #0a0068 90%)",
                        },
                        "&:disabled": {
                            opacity: 0.5
                        }
                    }}
                >
                    {existingRatingId ? 'Update Rating' : 'Submit Rating'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RatingDialog;
